import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getVarianceReportDataV2,
  getVehicleTrackingWorkflows,
  getBusinessPartnersDropdownOptions,
} from 'src/redux/Actions/VehicleTracking';
import { getPaginatedProducts } from 'src/redux/Actions/Settings';
import 'src/style/vehicleTrackingList.css';

import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import TableContent from 'src/components/VarianceReportV2/List/TableContent';
import Table from 'src/components/Vehicle/List/Table';
import getColorCode from 'src/utils/GetThemeColor';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import usePageTitle from 'src/utils/SetPageTitle';
import { loadStyledExcelExportModules } from 'src/utils/excelExport';

const VARIANCE_REPORT_PREFERENCE_KEY = 'savedVarianceReportColumns';
const DEFAULT_FIELDS = ['1', '2', '3', '4', '5', '6', '7'];

export default function VarianceReportV2() {
  usePageTitle('Variance Report');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');

  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState(null);
  const searchTexts = ['Vehicle Tracking Code', 'PO Number'];
  const [searchInputChange, setSearchInputChange] = useState(searchTexts[0]);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [showFunnel, setShowFunnel] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    exitStartDate: null,
    exitEndDate: null,
    selectedWorkflowIdList: [],
    selectedMaterialCodeList: [],
    selectedSupplierList: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    exitStartDate: null,
    exitEndDate: null,
    selectedWorkflowIdList: [],
    selectedMaterialCodeList: [],
    selectedSupplierList: [],
  });

  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const organisationId = authReducer?.userData?.organisations?.id;
  const tenantId = authReducer?.userData?.tenant?.id;

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const settingsReducer = useSelector((state) => state.SettingsReducer);

  const { varianceReportDataV2, workflows, businessPartnersDropdown } =
    vehicleTrackingDataReducer;
  const { productList } = settingsReducer;

  const fieldFilter = varianceReportDataV2?.fieldMapping || [];
  const selectedFields = varianceReportDataV2?.selectedFields || [];
  const varianceReport = varianceReportDataV2?.varianceReport || [];
  const totalPages = varianceReportDataV2?.totalPages || 1;

  const workflowDataOptions = workflows.map((workflowObj) => ({
    label: workflowObj.name,
    value: workflowObj.id,
  }));
  const materialCodeDataOptions = productList.map((productObj) => ({
    label: productObj.name,
    value: productObj.id,
  }));
  const supplierDataOptions = businessPartnersDropdown.map((supplierObj) => ({
    label: `${supplierObj.business_partner_sap_code} - ${supplierObj.name}`,
    value: supplierObj.id,
  }));

  // ---- column preferences (same pattern as VehicleList) ----
  const savedPreferences = (() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(VARIANCE_REPORT_PREFERENCE_KEY),
      );
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  })();
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      effectiveFieldOrder
        .map((fieldId) => {
          const selectedField = selectedFields.find(
            (field) => String(field.id) === String(fieldId),
          );
          return selectedField
            ? { ...selectedField, id: fieldId, columnId: fieldId }
            : null;
        })
        .filter(Boolean),
    [effectiveFieldOrder, selectedFields],
  );

  useEffect(() => {
    localStorage.setItem(
      VARIANCE_REPORT_PREFERENCE_KEY,
      JSON.stringify({
        order: draggedFieldOrder,
        selected: selectedFieldFilter,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  // ---- search debounce (same pattern as VehicleList) ----
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText || null);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchInputChange(
        (prev) =>
          searchTexts[(searchTexts.indexOf(prev) + 1) % searchTexts.length],
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // ---- filters: draft (formData) vs applied (appliedFilters) ----
  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleResetFilter = () => {
    const emptyFilters = {
      startDate: null,
      endDate: null,
      exitStartDate: null,
      exitEndDate: null,
      selectedWorkflowIdList: [],
      selectedMaterialCodeList: [],
      selectedSupplierList: [],
    };
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageNumber(1);
  };

  const handleFilterSubmit = () => {
    setAppliedFilters(formData);
    setPageNumber(1);
    if (isMobile) setShowFunnel(false);
  };

  // ---- fetch, same as VehicleList: date params via URLSearchParams,
  // id lists passed as arrays, appended in the API layer ----
  const fetchVarianceReport = useCallback(() => {
    if (!organisationId) return;

    const params = new URLSearchParams();
    if (appliedFilters.startDate)
      params.append('start_date', appliedFilters.startDate);
    if (appliedFilters.endDate)
      params.append('end_date', appliedFilters.endDate);
    if (appliedFilters.exitStartDate)
      params.append('exit_start_date', appliedFilters.exitStartDate);
    if (appliedFilters.exitEndDate)
      params.append('exit_end_date', appliedFilters.exitEndDate);

    const workflowIdList =
      appliedFilters.selectedWorkflowIdList?.map((item) => item.value) || [];
    const materialCodeList =
      appliedFilters.selectedMaterialCodeList?.map((item) => item.value) || [];
    const supplierList =
      appliedFilters.selectedSupplierList?.map((item) => item.value) || [];

    dispatch(
      getVarianceReportDataV2(
        organisationId,
        pageNumber,
        searchInput,
        paginationBatchSize,
        workflowIdList,
        materialCodeList,
        supplierList,
        params.toString(),
        effectiveFieldOrder,
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    organisationId,
    pageNumber,
    paginationBatchSize,
    refreshKey,
    searchInput,
  ]);

  useEffect(() => {
    fetchVarianceReport();
  }, [fetchVarianceReport]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };
  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber(pageNumber + 1);
  };
  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  const hasUnappliedFilters = () =>
    Object.keys(formData).some(
      (key) =>
        JSON.stringify(formData[key]) !== JSON.stringify(appliedFilters[key]),
    );

  async function exportToExcel(headers, varianceReportData) {
    if (!varianceReportData?.length) return;
    const { utils, write, saveAs } = await loadStyledExcelExportModules();

    const headerRow = headers.map((header) => header.label);
    const headerKeys = headers.map((header) => header.value);

    const dataRows = varianceReportData.map((varianceRow) =>
      headerKeys.map((key) => {
        const value = varianceRow[key];
        return Array.isArray(value) ? value.join('\r\n') : value ?? '';
      }),
    );

    const today = new Date();
    const formattedToday = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

    const sheetContent = [
      [authReducer.userData.organisations?.name || 'Organisation Name'],
      ['Weight Bridge Variance Register'],
      [
        `Reporting Period: ${appliedFilters.startDate} to ${appliedFilters.endDate || formattedToday}`,
      ],
      [],
      headerRow,
      ...dataRows,
    ];

    const worksheet = utils.aoa_to_sheet(sheetContent);
    const totalColumns = headerRow.length;
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: totalColumns - 1 } },
    ];
    worksheet['!cols'] = headerKeys.map((_, colIndex) => {
      const maxLen = [headerRow, ...dataRows].reduce((max, row) => {
        const cell = row[colIndex];
        const value = typeof cell === 'string' ? cell : String(cell ?? '');
        const lines = value.split('\r\n');
        const longestLine = Math.max(...lines.map((line) => line.length));
        return Math.max(max, longestLine);
      }, 10);
      return { wch: maxLen + 2 };
    });
    worksheet['!rows'] = sheetContent.map((row) => ({
      hpt:
        20 +
        (row.some((cell) => typeof cell === 'string' && cell.includes('\r\n'))
          ? 20
          : 0),
    }));

    const range = utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; R += 1) {
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const cellRef = utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellRef];
        if (cell) {
          const style = {
            alignment: {
              wrapText: true,
              vertical: 'middle',
              horizontal: 'left',
            },
            font: { name: 'Calibri', sz: 11 },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          };
          if (R <= 2) {
            style.font.bold = true;
            style.font.sz = 14;
          }
          if (R === 4) {
            style.font.bold = true;
            style.fill = { fgColor: { rgb: 'DCE6F1' } };
            style.alignment.horizontal = 'center';
          }
          if (R > 4) style.alignment.horizontal = 'center';
          cell.s = style;
        }
      }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Variance Report');
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Variance_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }

  useSetBreadcrumbData(location.pathname, 'Variance Report');

  return (
    <div className="div-flex-column" data-testid="variance-report-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                className="form-input-section input-icon-remove"
                placeholder={`Search by ${searchInputChange}`}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">
            Variance Report
          </span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);
                      if (isMobile) setShowFunnel(true);
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{ keepMounted: false }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={() => exportToExcel(tableHeaders, varianceReport)}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={paginationBatchSize}
                onChange={(event) => {
                  setPaginationBatchSize(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={
                <TableContent
                  headers={tableHeaders}
                  varianceReport={varianceReport}
                />
              }
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div className="filter-list-item div-flex-row div-space-between div-align-center border-bottom">
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column rg-15">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Select Workflow
                    </span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti
                        name="selectedWorkflowIdList"
                        className="multi-select-input-section"
                        onChange={(selectedOption) =>
                          handleFilterInputChange({
                            target: {
                              name: 'selectedWorkflowIdList',
                              value: selectedOption,
                            },
                          })
                        }
                        onFocus={() =>
                          dispatch(
                            getVehicleTrackingWorkflows(organisationId, '1'),
                          )
                        }
                        value={formData.selectedWorkflowIdList || ''}
                        options={workflowDataOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Workflow"
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Material Code
                    </span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti
                        name="selectedMaterialCodeList"
                        className="multi-select-input-section"
                        onChange={(selectedOption) =>
                          handleFilterInputChange({
                            target: {
                              name: 'selectedMaterialCodeList',
                              value: selectedOption,
                            },
                          })
                        }
                        onFocus={() =>
                          dispatch(getPaginatedProducts(tenantId, ''))
                        }
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change')
                            dispatch(
                              getPaginatedProducts(tenantId, inputValue),
                            );
                        }}
                        value={formData.selectedMaterialCodeList || ''}
                        options={materialCodeDataOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Material Code"
                        filterOption={() => true}
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Supplier</span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti
                        name="selectedSupplierList"
                        className="multi-select-input-section"
                        onChange={(selectedOption) =>
                          handleFilterInputChange({
                            target: {
                              name: 'selectedSupplierList',
                              value: selectedOption,
                            },
                          })
                        }
                        onFocus={() =>
                          dispatch(
                            getBusinessPartnersDropdownOptions(
                              organisationId,
                              '',
                            ),
                          )
                        }
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            dispatch(
                              getBusinessPartnersDropdownOptions(
                                organisationId,
                                inputValue,
                              ),
                            );
                          }
                        }}
                        value={formData.selectedSupplierList || ''}
                        options={supplierDataOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Supplier"
                        filterOption={() => true}
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Start Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate || ''}
                        onChange={handleFilterInputChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate || ''}
                        onChange={handleFilterInputChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Exit - Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="exitStartDate"
                        value={formData.exitStartDate || ''}
                        onChange={handleFilterInputChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Exit - End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="exitEndDate"
                        value={formData.exitEndDate || ''}
                        onChange={handleFilterInputChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleFilterSubmit}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
        <Drawer
          anchor="right"
          open={isMobile && showFunnel}
          onClose={() => setShowFunnel(false)}
          ModalProps={{ keepMounted: false }}
          sx={{ zIndex: 2 }}
        >
          <div className="right-drawer-component-small">
            <div className="div-flex-column div-space-between rg-20">
              <div className="filter-list-item div-flex-row div-space-between div-align-center border-bottom">
                <span className="text-sm text-semi-bold">Filter</span>
              </div>
              <div className="filter-list-item-container-scroll div-flex-column rg-15">
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">
                    Select Workflow
                  </span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti
                      name="selectedWorkflowIdList"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'selectedWorkflowIdList',
                            value: selectedOption,
                          },
                        })
                      }
                      onFocus={() =>
                        dispatch(
                          getVehicleTrackingWorkflows(organisationId, '1'),
                        )
                      }
                      value={formData.selectedWorkflowIdList || ''}
                      options={workflowDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Workflow"
                    />
                    <span className="form-input-section-icon">
                      <Search className="icon-size-sm" color="#9F9F9F" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Material Code</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti
                      name="selectedMaterialCodeList"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'selectedMaterialCodeList',
                            value: selectedOption,
                          },
                        })
                      }
                      onFocus={() =>
                        dispatch(getPaginatedProducts(tenantId, ''))
                      }
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change')
                          dispatch(getPaginatedProducts(tenantId, inputValue));
                      }}
                      value={formData.selectedMaterialCodeList || ''}
                      options={materialCodeDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Material Code"
                      filterOption={() => true}
                    />
                    <span className="form-input-section-icon">
                      <Search className="icon-size-sm" color="#9F9F9F" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Supplier</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti
                      name="selectedSupplierList"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'selectedSupplierList',
                            value: selectedOption,
                          },
                        })
                      }
                      onFocus={() =>
                        dispatch(
                          getBusinessPartnersDropdownOptions(
                            organisationId,
                            '',
                          ),
                        )
                      }
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          dispatch(
                            getBusinessPartnersDropdownOptions(
                              organisationId,
                              inputValue,
                            ),
                          );
                        }
                      }}
                      value={formData.selectedSupplierList || ''}
                      options={supplierDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Supplier"
                      filterOption={() => true}
                    />
                    <span className="form-input-section-icon">
                      <Search className="icon-size-sm" color="#9F9F9F" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Start Date</span>
                  <div className="input-wrapper">
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate || ''}
                      onChange={handleFilterInputChange}
                      className="form-input-section input-icon-date"
                    />
                    <span className="form-input-section-icon">
                      <Calendar
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">End Date</span>
                  <div className="input-wrapper">
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate || ''}
                      onChange={handleFilterInputChange}
                      className="form-input-section input-icon-date"
                    />
                    <span className="form-input-section-icon">
                      <Calendar
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">
                    Exit - Start Date
                  </span>
                  <div className="input-wrapper">
                    <input
                      type="date"
                      name="exitStartDate"
                      value={formData.exitStartDate || ''}
                      onChange={handleFilterInputChange}
                      className="form-input-section input-icon-date"
                    />
                    <span className="form-input-section-icon">
                      <Calendar
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">
                    Exit - End Date
                  </span>
                  <div className="input-wrapper">
                    <input
                      type="date"
                      name="exitEndDate"
                      value={formData.exitEndDate || ''}
                      onChange={handleFilterInputChange}
                      className="form-input-section input-icon-date"
                    />
                    <span className="form-input-section-icon">
                      <Calendar
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                <button
                  className={`common-btn-outlined border-${themeColor}`}
                  onClick={handleResetFilter}
                >
                  Reset
                </button>
                <button
                  className={`common-btn theme-background-${themeColor}`}
                  onClick={handleFilterSubmit}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Drawer>
      </div>
    </div>
  );
}
